"use client";

import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle2,
  FileText,
  Image as ImageIcon,
  Info,
  Laptop,
  Loader2,
  Phone,
  Plus,
  Send,
  Smartphone,
  Upload,
  UploadCloud,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { apiPatch, apiPost } from "@/services/api";
import type { Product } from "@/types";

interface CustomerListingFormProps {
  initialData?: Product | null;
  isEditing?: boolean;
  onSuccess?: (product: Product) => void;
  onCancel?: () => void;
  onPhoneChange?: (phone: string) => void;
}

const BRAND_PRESETS = [
  "Apple",
  "Samsung",
  "Dell",
  "HP",
  "Lenovo",
  "Asus",
  "Tecno",
  "Infinix",
  "Xiaomi",
  "Acer",
  "Google",
  "Huawei",
  "OnePlus",
  "Microsoft",
  "Other",
];

const CONDITION_PRESETS = [
  { value: "Excellent Condition (Like New)", label: "Excellent Condition (Like New)" },
  { value: "Brand New (Sealed Pack)", label: "Brand New (Sealed Pack)" },
  { value: "Good (Light usage scratches)", label: "Good (Light usage scratches)" },
  { value: "Fair (Visible signs of wear)", label: "Fair (Visible signs of wear)" },
  { value: "Defective / For Spare Parts", label: "Defective / For Spare Parts" },
];

function compressImageFile(file: File, maxWidth = 1280, quality = 0.82): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("File is not an image"));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read image file"));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Failed to load image"));
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth || height > maxWidth) {
          if (width > height) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxWidth) / height);
            height = maxWidth;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(e.target?.result as string);
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(dataUrl);
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
}

export function CustomerListingForm({
  initialData,
  isEditing = false,
  onSuccess,
  onCancel,
  onPhoneChange,
}: CustomerListingFormProps) {
  const [title, setTitle] = useState(initialData?.title || "");
  const [category, setCategory] = useState<string>(() => {
    if (initialData?.category) {
      if (
        initialData.category.toLowerCase().includes("phone") ||
        initialData.category.toLowerCase().includes("mobile")
      ) {
        return "Mobiles & Accessories";
      }
      return "Laptops & Computers";
    }
    return "Mobiles & Accessories";
  });
  const [brand, setBrand] = useState(() => {
    if (initialData?.brand) {
      const match = BRAND_PRESETS.find((b) => b.toLowerCase() === initialData.brand.toLowerCase());
      return match || "Other";
    }
    return "Apple";
  });
  const [customBrand, setCustomBrand] = useState(() => {
    if (initialData?.brand && !BRAND_PRESETS.some((b) => b.toLowerCase() === initialData.brand.toLowerCase())) {
      return initialData.brand;
    }
    return "";
  });
  const [price, setPrice] = useState(initialData?.price ? String(initialData.price) : "");
  const [mrp, setMrp] = useState(initialData?.mrp ? String(initialData.mrp) : "");
  const [sellerPhone, setSellerPhone] = useState(initialData?.sellerPhone || "");
  const [description, setDescription] = useState(initialData?.description || "");
  const [condition, setCondition] = useState(() => {
    const found = initialData?.tags?.find((t) =>
      CONDITION_PRESETS.some((cp) => cp.value.toLowerCase().includes(t.toLowerCase()))
    );
    return found || "Excellent Condition (Like New)";
  });
  const [images, setImages] = useState<string[]>(() => {
    if (initialData?.images && initialData.images.length > 0) {
      return initialData.images;
    }
    return [];
  });
  const [newImageUrl, setNewImageUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isProcessingFiles, setIsProcessingFiles] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    onPhoneChange?.(sellerPhone);
  }, [sellerPhone, onPhoneChange]);

  function handlePhoneChange(val: string) {
    setSellerPhone(val);
    setErrors((prev) => ({ ...prev, sellerPhone: "" }));
    onPhoneChange?.(val);
  }

  async function handleFiles(fileList: FileList | File[] | null) {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) {
      toast.error("Please select valid image files (JPG, PNG, WebP, etc.)");
      return;
    }

    setIsProcessingFiles(true);
    const toastId = toast.loading(`Optimizing ${files.length} photo${files.length > 1 ? "s" : ""}...`);
    try {
      const processed: string[] = [];
      for (const file of files) {
        try {
          const dataUrl = await compressImageFile(file);
          processed.push(dataUrl);
        } catch (err) {
          console.error("Failed to process file:", file.name, err);
        }
      }

      if (processed.length > 0) {
        setImages((prev) => {
          const next = [...prev];
          for (const item of processed) {
            if (!next.includes(item)) {
              next.push(item);
            }
          }
          return next;
        });
        setErrors((prev) => {
          const next = { ...prev };
          delete next.images;
          return next;
        });
        toast.success(`Added ${processed.length} photo${processed.length > 1 ? "s" : ""} from your device!`, {
          id: toastId,
        });
      } else {
        toast.error("Could not process selected photos", { id: toastId });
      }
    } catch {
      toast.error("Error processing photos", { id: toastId });
    } finally {
      setIsProcessingFiles(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }

  function addImage(url: string) {
    const trimmed = url.trim();
    if (!trimmed) return;
    if (images.includes(trimmed)) {
      toast.error("Image already added");
      return;
    }
    setImages((prev) => [...prev, trimmed]);
    setNewImageUrl("");
    setErrors((prev) => {
      const next = { ...prev };
      delete next.images;
      return next;
    });
  }

  function removeImage(index: number) {
    setImages((prev) => prev.filter((_, i) => i !== index));
  }

  function validate() {
    const errs: Record<string, string> = {};
    if (!title.trim() || title.trim().length < 3) {
      errs.title = "Title must be at least 3 characters long.";
    }
    const effectiveBrand = brand === "Other" ? customBrand.trim() : brand.trim();
    if (!effectiveBrand) {
      errs.brand = "Brand is required.";
    }
    const numPrice = Number(price);
    if (!price || isNaN(numPrice) || numPrice <= 0) {
      errs.price = "Enter a valid positive price.";
    }
    if (!sellerPhone.trim() || sellerPhone.trim().length < 6) {
      errs.sellerPhone = "Valid contact phone number is required so buyers can contact you.";
    }
    if (images.length === 0) {
      errs.images = "Please add at least one product photo.";
    }
    if (!description.trim() || description.trim().length < 10) {
      errs.description = "Provide a description of at least 10 characters.";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setSubmitting(true);
    try {
      const effectiveBrand = brand === "Other" ? customBrand.trim() : brand.trim();
      const numPrice = Number(price);
      const numMrp = mrp && Number(mrp) > numPrice ? Number(mrp) : numPrice;

      const isMobile =
        category.toLowerCase().includes("mobile") ||
        category.toLowerCase().includes("phone");
      const targetCategory = isMobile ? "Mobiles & Accessories" : "Laptops & Computers";

      const payload = {
        title: title.trim(),
        category: targetCategory,
        brand: effectiveBrand,
        price: numPrice,
        mrp: numMrp,
        sellerPhone: sellerPhone.trim(),
        description: description.trim(),
        images,
        tags: [
          isMobile ? "phone" : "pc",
          isMobile ? "phones" : "laptop",
          isMobile ? "mobile" : "laptops",
          isMobile ? "mobiles" : "computer",
          isMobile ? "smartphone" : "computers",
          isMobile ? "smartphones" : "desktop",
          isMobile ? "mobiles & accessories" : "laptops & computers",
          effectiveBrand.toLowerCase(),
          condition,
          "marketplace",
        ],
      };

      if (isEditing && initialData?.id) {
        const res = await apiPatch<{ product: Product; message: string }>(
          `/api/marketplace/listings/${initialData.id}`,
          payload
        );
        toast.success(res?.message || "Listing updated and submitted for review!");
        if (onSuccess && res?.product) {
          onSuccess(res.product);
        }
      } else {
        const res = await apiPost<{ product: Product; message: string }>(
          "/api/marketplace/listings",
          payload
        );
        toast.success(res?.message || "Listing submitted for review!");
        if (onSuccess && res?.product) {
          onSuccess(res.product);
        }
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not submit listing.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="rounded-3xl border border-slate-200/90 bg-white p-6 sm:p-9 shadow-sm dark:border-white/10 dark:bg-slate-900">
      {/* Form Header Bar */}
      <div className="flex flex-col gap-3 pb-6 border-b border-slate-100 sm:flex-row sm:items-center sm:justify-between dark:border-white/10">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight dark:text-white">
            {isEditing ? "Edit & Resubmit Listing" : "Create Your Phone or PC Listing"}
          </h2>
          <p className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            Fill out technical specs, upload high-res photos, set price and phone number.
          </p>
        </div>

        <div className="inline-flex items-center gap-1.5 self-start rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
          <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
          <span>Quick Admin Review</span>
        </div>
      </div>

      {/* Trust & Verification Callout */}
      <div className="mt-6 flex items-start gap-3 rounded-2xl border border-sky-200/80 bg-sky-50/70 p-4 text-xs text-sky-900 dark:border-sky-900/40 dark:bg-sky-950/30 dark:text-sky-200">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#087DB5] dark:text-[#00B7B0]" />
        <div className="leading-relaxed">
          <strong className="font-bold">Free Community Marketplace Policy:</strong> Posting is completely free for phones, laptops, and PCs. Once submitted, your listing is set to <em>Pending Review</em> by our team before display.
        </div>
      </div>

      {/* Rejection Notice if editing a rejected listing */}
      {isEditing && initialData?.status === "REJECTED" && (
        <div className="mt-4 rounded-2xl border border-red-300 bg-red-50 p-4 text-xs text-red-950 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
            <div>
              <p className="font-bold text-sm text-red-900 dark:text-red-200">Previous Rejection Reason:</p>
              <p className="mt-1 font-medium text-red-800 dark:text-red-300">
                {initialData.rejectionReason || "Listing required revision."}
              </p>
              <p className="mt-1 text-[11px] text-red-700 dark:text-red-400">
                Make your corrections below and submit. Your listing will automatically return to Pending status for admin re-evaluation.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">
        {/* STEP 1: CATEGORY SELECTION */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
            1. Select Category <span className="text-red-500">*</span>
          </label>

          <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {/* Phones & Mobiles Card */}
            <div
              onClick={() => setCategory("Mobiles & Accessories")}
              className={`relative flex items-center gap-3.5 rounded-2xl border-2 p-4 cursor-pointer transition-all ${
                category === "Mobiles & Accessories"
                  ? "border-[#087DB5] bg-sky-50/50 shadow-xs dark:bg-sky-950/20"
                  : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50 dark:border-white/10 dark:bg-slate-900 dark:hover:bg-slate-800/50"
              }`}
            >
              <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors ${
                  category === "Mobiles & Accessories"
                    ? "bg-[#087DB5] text-white"
                    : "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-slate-300"
                }`}
              >
                <Smartphone className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="text-sm font-bold text-slate-900 dark:text-white">Phones</p>
                  <span className="rounded bg-sky-100 dark:bg-sky-950/70 text-[#087DB5] dark:text-[#00B7B0] px-1.5 py-0.5 text-[10px] font-bold">
                    Mobiles &amp; Accessories
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Smartphones, accessories, tablets</p>
              </div>
              <div
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                  category === "Mobiles & Accessories"
                    ? "border-[#087DB5] bg-[#087DB5] text-white"
                    : "border-slate-300 dark:border-slate-600"
                }`}
              >
                {category === "Mobiles & Accessories" && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
              </div>
            </div>

            {/* PCs & Laptops Card */}
            <div
              onClick={() => setCategory("Laptops & Computers")}
              className={`relative flex items-center gap-3.5 rounded-2xl border-2 p-4 cursor-pointer transition-all ${
                category === "Laptops & Computers"
                  ? "border-[#087DB5] bg-sky-50/50 shadow-xs dark:bg-sky-950/20"
                  : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50 dark:border-white/10 dark:bg-slate-900 dark:hover:bg-slate-800/50"
              }`}
            >
              <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors ${
                  category === "Laptops & Computers"
                    ? "bg-[#087DB5] text-white"
                    : "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-slate-300"
                }`}
              >
                <Laptop className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="text-sm font-bold text-slate-900 dark:text-white">PCs &amp; Laptops</p>
                  <span className="rounded bg-sky-100 dark:bg-sky-950/70 text-[#087DB5] dark:text-[#00B7B0] px-1.5 py-0.5 text-[10px] font-bold">
                    Laptops &amp; Computers
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Desktop PCs, laptops, computers, workstations</p>
              </div>
              <div
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                  category === "Laptops & Computers"
                    ? "border-[#087DB5] bg-[#087DB5] text-white"
                    : "border-slate-300 dark:border-slate-600"
                }`}
              >
                {category === "Laptops & Computers" && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
              </div>
            </div>
          </div>
        </div>

        {/* STEP 2: TITLE & BRAND */}
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
              2. Listing Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setErrors((prev) => ({ ...prev, title: "" }));
              }}
              placeholder={
                category === "Mobiles & Accessories"
                  ? "e.g. iPhone 14 Pro Max 256GB Space Black - Unlocked"
                  : "e.g. Dell XPS 15 9520 Core i7 16GB RAM 512GB SSD RTX 3050"
              }
              className={`mt-1.5 h-11 w-full rounded-xl border bg-slate-50/70 px-3.5 text-sm text-slate-900 outline-none transition focus:bg-white focus:ring-2 focus:ring-[#087DB5]/20 dark:bg-slate-800 dark:text-white ${
                errors.title
                  ? "border-red-500 focus:border-red-500"
                  : "border-slate-300 hover:border-slate-400 focus:border-[#087DB5] dark:border-white/10"
              }`}
            />
            <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
              Include model name, storage size, and color for faster search indexing.
            </p>
            {errors.title && <p className="mt-1 text-xs text-red-500">{errors.title}</p>}
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
              Brand <span className="text-red-500">*</span>
            </label>
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-slate-50/70 px-3 text-sm text-slate-900 outline-none transition hover:border-slate-400 focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
            >
              {BRAND_PRESETS.map((b) => (
                <option key={b} value={b}>
                  {b === "Other" ? "Other Brand…" : b}
                </option>
              ))}
            </select>
            {brand === "Other" && (
              <input
                type="text"
                value={customBrand}
                onChange={(e) => setCustomBrand(e.target.value)}
                placeholder="Enter brand name"
                className="mt-2 h-9 w-full rounded-lg border border-slate-300 bg-white px-3 text-xs text-slate-900 outline-none focus:border-[#087DB5] dark:border-white/10 dark:bg-slate-800 dark:text-white"
              />
            )}
          </div>
        </div>

        {/* STEP 3: CONDITION & DIRECT CONTACT PHONE */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
              Condition <span className="text-red-500">*</span>
            </label>
            <select
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-slate-50/70 px-3 text-sm text-slate-900 outline-none transition hover:border-slate-400 focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
            >
              {CONDITION_PRESETS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
              Seller Contact Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="relative mt-1.5">
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-600 dark:text-slate-300">
                🇪🇹 +251
              </span>
              <input
                type="tel"
                value={sellerPhone.replace(/^\+?251\s?/, "")}
                onChange={(e) => {
                  const cleaned = e.target.value;
                  const formatted = cleaned.startsWith("+251") ? cleaned : `+251 ${cleaned.trim()}`;
                  handlePhoneChange(formatted);
                }}
                placeholder="91 123 4567"
                className={`h-11 w-full rounded-xl border bg-slate-50/70 pl-20 pr-3.5 text-sm font-semibold text-slate-900 outline-none transition focus:bg-white focus:ring-2 focus:ring-emerald-500/20 dark:bg-slate-800 dark:text-white ${
                  errors.sellerPhone
                    ? "border-red-500 focus:border-red-500"
                    : "border-slate-300 hover:border-slate-400 focus:border-emerald-500 dark:border-white/10"
                }`}
              />
            </div>
            <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
              This number is shown publicly so buyers can call or text you directly.
            </p>
            {errors.sellerPhone && <p className="mt-1 text-xs text-red-500">{errors.sellerPhone}</p>}
          </div>
        </div>

        {/* STEP 4: PRICING */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
              Your Selling Price (ETB) <span className="text-red-500">*</span>
            </label>
            <div className="relative mt-1.5">
              <input
                type="number"
                step="any"
                min="1"
                value={price}
                onChange={(e) => {
                  setPrice(e.target.value);
                  setErrors((prev) => ({ ...prev, price: "" }));
                }}
                placeholder="45000"
                className={`h-11 w-full rounded-xl border bg-slate-50/70 pl-3.5 pr-14 text-sm font-bold text-slate-900 outline-none transition focus:bg-white focus:ring-2 focus:ring-[#087DB5]/20 dark:bg-slate-800 dark:text-white ${
                  errors.price
                    ? "border-red-500 focus:border-red-500"
                    : "border-slate-300 hover:border-slate-400 focus:border-[#087DB5] dark:border-white/10"
                }`}
              />
              <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                ETB
              </span>
            </div>
            {errors.price && <p className="mt-1 text-xs text-red-500">{errors.price}</p>}
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
              Original Market Price / M.R.P. (Optional)
            </label>
            <div className="relative mt-1.5">
              <input
                type="number"
                step="any"
                min="1"
                value={mrp}
                onChange={(e) => setMrp(e.target.value)}
                placeholder="52000"
                className="h-11 w-full rounded-xl border border-slate-300 bg-slate-50/70 pl-3.5 pr-14 text-sm text-slate-900 outline-none transition hover:border-slate-400 focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
              />
              <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                ETB
              </span>
            </div>
            <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
              Shows a discount badge if higher than your selling price.
            </p>
          </div>
        </div>

        {/* STEP 5: PRODUCT PHOTOS */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
              Product Images <span className="text-red-500">*</span> ({images.length} added)
            </label>
          </div>

          {/* Hidden Direct File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />

          {/* Upload Drop Zone Box */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              if (e.dataTransfer.files) {
                handleFiles(e.dataTransfer.files);
              }
            }}
            className={`group flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer transition-all ${
              isDragging
                ? "border-[#087DB5] bg-sky-50 shadow-xs scale-[1.01] dark:bg-sky-950/40"
                : "border-slate-300 bg-slate-50/60 hover:border-[#087DB5] hover:bg-sky-50/30 dark:border-white/15 dark:bg-slate-800/40"
            }`}
          >
            {isProcessingFiles ? (
              <>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 text-[#087DB5] dark:bg-sky-950/60 dark:text-[#00B7B0]">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
                <p className="mt-2.5 text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200">
                  Processing &amp; optimizing device photos…
                </p>
                <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
                  Resizing and preparing high-resolution images for fast preview
                </p>
              </>
            ) : (
              <>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 text-[#087DB5] transition group-hover:scale-110 dark:bg-sky-950/60 dark:text-[#00B7B0]">
                  <UploadCloud className="h-6 w-6" />
                </div>
                <p className="mt-2.5 text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200">
                  Click to browse or drop device photos here
                </p>
                <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
                  Select photos directly from your phone, laptop, or camera storage.
                </p>
              </>
            )}
          </div>

          {/* URL Attachment Fallback Bar */}
          <div className="mt-3 flex gap-2">
            <input
              type="url"
              value={newImageUrl}
              onChange={(e) => setNewImageUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addImage(newImageUrl);
                }
              }}
              placeholder="Or paste public image link (e.g. https://...)"
              className="h-10 flex-1 rounded-xl border border-slate-300 bg-slate-50/70 px-3.5 text-xs text-slate-900 outline-none transition hover:border-slate-400 focus:bg-white focus:border-[#087DB5] dark:border-white/10 dark:bg-slate-800 dark:text-white"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addImage(newImageUrl)}
              disabled={!newImageUrl.trim()}
              className="h-10 px-4 text-xs font-bold"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Image
            </Button>
          </div>

          {errors.images && <p className="mt-1.5 text-xs text-red-500">{errors.images}</p>}

          {/* Thumbnails preview list */}
          {images.length > 0 && (
            <div className="mt-3.5 flex flex-wrap gap-3">
              {images.map((img, idx) => (
                <div
                  key={idx}
                  className="group relative h-20 w-20 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-2xs dark:border-white/10 dark:bg-slate-800"
                >
                  <img
                    src={img}
                    alt={`Preview ${idx + 1}`}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLElement).style.opacity = "0.3";
                    }}
                  />
                  {idx === 0 && (
                    <span className="absolute bottom-1 left-1 rounded bg-black/75 px-1 py-0.2 text-[9px] font-bold text-white uppercase">
                      Main
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-white opacity-0 transition group-hover:opacity-100 hover:scale-110 shadow-sm"
                    title="Remove image"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* STEP 6: DESCRIPTION */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
            Description &amp; Details <span className="text-red-500">*</span>
          </label>
          <textarea
            rows={4}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setErrors((prev) => ({ ...prev, description: "" }));
            }}
            placeholder="Describe your device condition, battery health %, storage capacity, accessories included (charger, box, case), reason for sale, and inspection/pickup details (e.g., Bole, Piassa, Kazanchis)..."
            className={`mt-1.5 w-full rounded-xl border bg-slate-50/70 p-3.5 text-sm text-slate-900 outline-none transition focus:bg-white focus:ring-2 focus:ring-[#087DB5]/20 dark:bg-slate-800 dark:text-white ${
              errors.description
                ? "border-red-500 focus:border-red-500"
                : "border-slate-300 hover:border-slate-400 focus:border-[#087DB5] dark:border-white/10"
            }`}
          />
          {errors.description && <p className="mt-1 text-xs text-red-500">{errors.description}</p>}
        </div>

        {/* FORM ACTIONS */}
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 pt-6 border-t border-slate-100 dark:border-white/10">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            By publishing, you agree to MOVA Market&apos;s Terms of Service &amp; anti-fraud rules.
          </p>

          <div className="flex items-center gap-3">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="h-11 px-7 bg-gradient-to-r from-[#087DB5] to-[#073B70] text-sm font-bold text-white shadow-md hover:from-[#098dc9] hover:to-[#084582] transition-all flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  {isEditing ? "Save & Resubmit For Review" : "Post Free Listing"}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
